import { Portal } from '@mantine/core'
import React from 'react'
import styled, { keyframes } from 'styled-components'

type TLoaderOverlay = {
  /** Show the over lay */
  isActive?: boolean

  /** Show the over lay */
  isLoading?: boolean

  /** Change size of spinner to be smaller */
  small?: boolean

  /** Adjust the opacity of the overlay */
  alpha?: number

  /** Hide the spinner icon */
  nospinner?: boolean

  /** zIndex of the overlay */
  zIndex?: number

  /** Speed of the spinner */
  speed?: string

  /** Overlay all screen */
  fullsize?: boolean

  /** Overlay all screen */
  fullscreen?: boolean

  /** inner content of the overlay */
  children?: React.ReactNode

  /** Offset in px */
  offset?: number

  /** Additional style */
  style?: CSSStyleDeclaration
  opaque?: number
  offsetWidth?: number
  offsetHeight?: number
}
type TWrapper = {
  fullsize?: boolean
  offset?: number
  offsetHeight?: number
  offsetWidth?: number
  alpha?: string | number
  zIndex?: string | number
}

type TSpinner = {
  speed?: string
  size?: string
}
const Wrapper = styled.div<TWrapper>`
  display: flex;
  flex-direction: column;
  position: ${({ fullsize }) => (fullsize ? 'fixed' : 'absolute')};
  width: ${({ fullsize, offset, offsetWidth }) =>
    fullsize ? '100vw' : `calc(100% - ${offsetWidth || offset || 0}px)`};
  height: ${({ fullsize, offset, offsetHeight }) =>
    fullsize ? '100vh' : `calc(100% - ${offsetHeight || offset || 0}px)`};
  top: ${({ fullsize, offset, offsetHeight }) =>
    fullsize ? '0' : `${(offsetHeight || offset || 0) / 2}px`};
  right: ${({ fullsize, offset, offsetWidth }) =>
    fullsize ? '0' : `${(offsetWidth || offset || 0) / 2}px`};
  bottom: ${({ fullsize, offset, offsetHeight }) =>
    fullsize ? '0' : `${(offsetHeight || offset || 0) / 2}px`};
  left: ${({ fullsize, offset, offsetWidth }) =>
    fullsize ? '0' : `${(offsetWidth || offset || 0) / 2}px`};
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, ${({ alpha }) => alpha});
  z-index: ${({ zIndex, fullsize }) => (fullsize ? '5000' : zIndex || '2000')};
`

const SpinnerKeyframes = keyframes`
to {
  transform: rotate(1turn);
}
`

const Spinner = styled.div<TSpinner>`
  animation: ${SpinnerKeyframes} ${({ speed }) => speed}s infinite linear;
  width: ${({ size }) => size || 16}px;
  height: ${({ size }) => size || 16}px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid #ccc;
  border-right-color: #333;
  display: inline-block;
`

const LoaderOverlay = (props: TLoaderOverlay): React.ReactElement => {
  const {
    isActive,
    isLoading,
    small,
    alpha = 0.8,
    opaque,
    nospinner,
    zIndex,
    speed = '0.6',
    fullsize,
    fullscreen,
    children,
    offset,
    style = {},
    offsetWidth = undefined,
    offsetHeight = undefined,
  } = props

  if (!isActive && !isLoading) {
    return <></>
  }

  const inner = (
    <Wrapper
      alpha={opaque ? 1 : alpha}
      zIndex={zIndex}
      fullsize={fullsize || fullscreen}
      style={style}
      offset={offset}
      className="LoaderOverlay/Wrapper"
      offsetWidth={offsetWidth}
      offsetHeight={offsetHeight}
    >
      {!nospinner ? <Spinner size={small ? '20' : '40'} speed={speed} /> : null}
      <div>{children}</div>
    </Wrapper>
  )

  if (fullsize || fullscreen) {
    return <Portal>{inner}</Portal>
  }

  return inner
}

export default LoaderOverlay
